import React from "react";
import "./SourcesTrainer.css";

const SourcesTrainer = ({ apiBase, email, botName, planCharLimit = Infinity, onReindexed }) => {
  console.log("API Base", apiBase);
  const [tab, setTab] = React.useState("Links");
  const base = (apiBase || window.location.origin).replace(/\/$/, "");
  const BACKEND_URL = process.env.REACT_APP_BACKEND_API_URL;

  // Alerts
  const [alert, setAlert] = React.useState(null); // {type:'success'|'error'|'info', msg:string}

  // Busy flags
  const [submitting, setSubmitting] = React.useState(false);
  const [uploadBusy, setUploadBusy] = React.useState(false);
  const [loadingHistory, setLoadingHistory] = React.useState(false);

  // Links (new entry composer)
  const [linkInput, setLinkInput] = React.useState("");
  const [links, setLinks] = React.useState([]);

  // FAQs (new entry composer)
  const [faqQ, setFaqQ] = React.useState("");
  const [faqA, setFaqA] = React.useState("");

  // History (existing items already trained)
  const [history, setHistory] = React.useState({
    knowledgeBase: [], // [{url,title,text,ts}]
    fileTexts: [],     // [{filename,text,ts}]
    sheets: [],        // [{filename,text,ts}]
    faqs: []           // [{q,a,ts}]
  });
  const [usedChars, setUsedChars] = React.useState(0);
  const PLAN_UPLOAD_LIMITS = {
    none: 0,
    starter: 10_000_000,
    pro: 50_000_000,
    advanced: 100_000_000,
    enterprise: Infinity,
  };

  const [user, setUser] = React.useState(null);
  React.useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${BACKEND_URL}/api/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) throw new Error("Failed to load user");

        const data = await res.json();
        setUser(data);
      } catch (err) {
        console.error("Billing fetch error:", err);
      }
    };

    fetchUser();
  }, [base]);

  // Helpers
  const requireIdentity = () => {
    if (!email || !botName) {
      setAlert({ type: "error", msg: "Please set Email and Bot Name first (Connect tab)." });
      return false;
    }
    return true;
  };

  const notify = (type, msg) => setAlert({ type, msg });
  const clearAlert = () => setAlert(null);

  const addLink = () => {
    const u = (linkInput || "").trim();
    if (!u) return;
    try {
      // minimal url validation
      // eslint-disable-next-line no-new
      new URL(u);
      setLinks((prev) => [...prev, u]);
      setLinkInput("");
      clearAlert();
    } catch {
      notify("error", "Please enter a valid URL (e.g., https://example.com/page).");
    }
  };

  const removeLink = (i) => {
    setLinks((prev) => prev.filter((_, idx) => idx !== i));
  };

  const fetchHistory = async () => {
    if (!email || !botName) return;
    setLoadingHistory(true);
    try {
      const res = await fetch(
        `${base}/api/data/${encodeURIComponent(email)}/${encodeURIComponent(botName)}`
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => null);

        if (res.status === 403 && errData?.code === "UPLOAD_LIMIT_REACHED") {
          notify(
            "error",
            `Upload limit reached. Remaining characters: ${errData.remaining}`
          );
          return;
        }

        throw new Error(errData?.error || "Request failed");
      }
      const data = await res.json();
      // /api/data returns BSON-safe JSON or doc directly;
      // if wrapped, unwrap; else use as-is
      const doc = data?.items?.[0] || data;
      setHistory({
        knowledgeBase: doc.knowledgeBase || [],
        fileTexts: doc.fileTexts || [],
        sheets: doc.sheets || [],
        faqs: doc.faqs || []
      });
    } catch (e) {
      notify("error", `Could not load history: ${e.message || e}`);
    } finally {
      setLoadingHistory(false);
    }
  };

  React.useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, botName, base]);

  // allow parent to re-trigger history refresh if it calls onReindexed
  const afterReindex = () => {
    fetchHistory();
    onReindexed?.();
  };
  React.useEffect(() => {
    if (user?.billing?.usage?.uploadChars != null) {
      setUsedChars(user.billing.usage.uploadChars);
    }
  }, [user]);
  // === Plan-based upload character limit guard ===
  const totalLimit = user?.billing?.limits?.uploadChars ?? 0;

  const atCharLimit =
    totalLimit > 0 && usedChars >= totalLimit;

  const charLimitDisplay =
    totalLimit > 0
      ? `${(totalLimit / 1_000_000).toFixed(0)}M`
      : "0M";

  const usedCharsDisplay = `${(usedChars / 1000).toFixed(2)}K`;

  const ensureWithinPlan = () => {
    if (!atCharLimit) return true;

    notify(
      "error",
      `Upload character limit reached (${usedCharsDisplay} / ${charLimitDisplay}). Please remove some data or upgrade your plan.`
    );
    return false;
  };

  const submitLinks = async () => {
    if (!requireIdentity()) return;
    if (links.length === 0) {
      return notify("error", "Add at least one URL before submitting.");
    }
    if (!ensureWithinPlan()) return;
    setSubmitting(true);
    clearAlert();
    try {
      const res = await fetch(
        `${base}/api/train/${encodeURIComponent(email)}/${encodeURIComponent(botName)}/links`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ links })
        }
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => null);

        if (res.status === 403 && errData?.code === "UPLOAD_LIMIT_REACHED") {
          notify(
            "error",
            `Upload limit reached. Remaining characters: ${errData.remaining}`
          );
          return;
        }

        throw new Error(errData?.error || "Request failed");
      }
      const data = await res.json();
      notify(
        "success",
        `Added ${data.added ?? links.length} link(s). Reindexed ${data.indexed_chunks ?? "—"} chunk(s).`
      );
      setLinks([]);
      afterReindex();
    } catch (e) {
      notify("error", `Link training failed: ${e.message || e}`);
    } finally {
      setSubmitting(false);
    }
  };

  const submitFaq = async (e) => {
    e.preventDefault();
    if (!requireIdentity()) return;
    if (!faqQ.trim() || !faqA.trim())
      return notify("error", "FAQ needs both Question and Answer.");
    if (!ensureWithinPlan()) return;
    setSubmitting(true);
    clearAlert();
    try {
      const fd = new FormData();
      fd.append("question", faqQ);
      fd.append("answer", faqA);
      const res = await fetch(
        `${base}/api/train/${encodeURIComponent(email)}/${encodeURIComponent(botName)}/faq`,
        { method: "POST", body: fd }
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => null);

        if (res.status === 403 && errData?.code === "UPLOAD_LIMIT_REACHED") {
          notify(
            "error",
            `Upload limit reached. Remaining characters: ${errData.remaining}`
          );
          return;
        }

        throw new Error(errData?.error || "Request failed");
      }
      const data = await res.json();
      notify("success", `FAQ added. Reindexed ${data.indexed_chunks ?? "—"} chunk(s).`);
      setFaqQ("");
      setFaqA("");
      afterReindex();
    } catch (e) {
      notify("error", `FAQ training failed: ${e.message || e}`);
    } finally {
      setSubmitting(false);
    }
  };

  const upload = async (endpoint, file) => {
    if (!requireIdentity()) return;
    if (!file) return;
    if (!ensureWithinPlan()) return;
    setUploadBusy(true);
    clearAlert();
    try {
      const fd = new FormData();
      fd.append(endpoint === "sheet" ? "sheet" : "file", file);
      const res = await fetch(
        `${base}/api/train/${encodeURIComponent(email)}/${encodeURIComponent(botName)}/${endpoint}`,
        { method: "POST", body: fd }
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => null);

        if (res.status === 403 && errData?.code === "UPLOAD_LIMIT_REACHED") {
          notify(
            "error",
            `Upload limit reached. Remaining characters: ${errData.remaining}`
          );
          return;
        }

        throw new Error(errData?.error || "Request failed");
      }
      const data = await res.json();
      const what = endpoint === "sheet" ? data.sheet : data.file;
      notify(
        "success",
        `${what ?? "Upload"} indexed. Reindexed ${data.indexed_chunks ?? "—"} chunk(s).`
      );
      afterReindex();
    } catch (e) {
      notify("error", `Upload failed: ${e.message || e}`);
    } finally {
      setUploadBusy(false);
    }
  };

  // Drag & drop for dropzones
  const onDrop = (e, type) => {
    e.preventDefault();
    if (!e.dataTransfer?.files?.length) return;
    const f = e.dataTransfer.files[0];
    if (type === "file") upload("file", f);
    if (type === "sheet") upload("sheet", f);
  };

  const preventDefault = (e) => e.preventDefault();

  // ----- History table helpers -----
  const fmtWhen = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const now = Date.now();
      const diff = Math.max(0, now - d.getTime());
      const mins = Math.round(diff / 60000);
      if (mins < 1) return "just now";
      if (mins < 60) return `${mins} min ago`;
      const hrs = Math.round(mins / 60);
      if (hrs < 24) return `${hrs} hr${hrs > 1 ? "s" : ""} ago`;
      return d.toLocaleDateString();
    } catch {
      return iso;
    }
  };

  const LinkHistory = () => (
    <div className="hist-block">
      <div className="hist-head">Previously added links</div>
      <div className="hist-table-wrap">
        <table className="hist-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Status</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {history.knowledgeBase.length === 0 ? (
              <tr>
                <td colSpan={4} className="empty-cell">
                  {loadingHistory ? "Loading…" : "No links yet"}
                </td>
              </tr>
            ) : (
              history.knowledgeBase.map((p, i) => (
                <tr key={i}>
                  <td title={p.url}>
                    <div className="cell-main">{p.title || p.url || "Untitled"}</div>
                    <div className="cell-sub">{p.url}</div>
                  </td>
                  <td>URL</td>
                  <td>
                    <span className="status trained">Trained</span>
                  </td>
                  <td>{fmtWhen(p.ts)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const FileHistory = () => (
    <div className="hist-block">
      <div className="hist-head">Previously uploaded files</div>
      <div className="hist-table-wrap">
        <table className="hist-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Status</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {history.fileTexts.length === 0 ? (
              <tr>
                <td colSpan={4} className="empty-cell">
                  {loadingHistory ? "Loading…" : "No files yet"}
                </td>
              </tr>
            ) : (
              history.fileTexts.map((f, i) => {
                const ext = (f.filename || "").split(".").pop()?.toUpperCase() || "TXT";
                return (
                  <tr key={i}>
                    <td>
                      <div className="cell-main">{f.filename}</div>
                    </td>
                    <td>{ext}</td>
                    <td>
                      <span className="status trained">Trained</span>
                    </td>
                    <td>{fmtWhen(f.ts)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const SheetHistory = () => (
    <div className="hist-block">
      <div className="hist-head">Previously uploaded spreadsheets</div>
      <div className="hist-table-wrap">
        <table className="hist-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Status</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {history.sheets.length === 0 ? (
              <tr>
                <td colSpan={4} className="empty-cell">
                  {loadingHistory ? "Loading…" : "No spreadsheets yet"}
                </td>
              </tr>
            ) : (
              history.sheets.map((s, i) => {
                const ext = (s.filename || "").split(".").pop()?.toUpperCase() || "CSV";
                return (
                  <tr key={i}>
                    <td>
                      <div className="cell-main">{s.filename}</div>
                    </td>
                    <td>{ext}</td>
                    <td>
                      <span className="status trained">Trained</span>
                    </td>
                    <td>{fmtWhen(s.ts)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const FaqHistory = () => (
    <div className="hist-block">
      <div className="hist-head">Previously added FAQs</div>
      <div className="hist-table-wrap">
        <table className="hist-table">
          <thead>
            <tr>
              <th>Question</th>
              <th>Status</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {history.faqs.length === 0 ? (
              <tr>
                <td colSpan={3} className="empty-cell">
                  {loadingHistory ? "Loading…" : "No FAQs yet"}
                </td>
              </tr>
            ) : (
              history.faqs.map((q, i) => (
                <tr key={i}>
                  <td>
                    <div className="cell-main">{q.q}</div>
                    {q.a ? <div className="cell-sub elide">{q.a}</div> : null}
                  </td>
                  <td>
                    <span className="status trained">Trained</span>
                  </td>
                  <td>{fmtWhen(q.ts)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="src-card">
      <div className="src-head">
        <div>
          <h2>Sources</h2>
          <p>Feed your bot with links, files, FAQs, and spreadsheets. We’ll index and retrain automatically.</p>
        </div>
        <div className="src-id">
          <span className="id-pill" title={email || "Email not set"}>📧 {email || "—"}</span>
          <span className="id-pill" title={botName || "Bot not set"}>🤖 {botName || "—"}</span>
        </div>
      </div>
      {/* Plan usage info */}
      <div
        style={{
          marginBottom: 12,
          fontSize: 13,
          color: "#6b7280",
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <span>
          <strong>Characters used:</strong> {usedCharsDisplay} / {charLimitDisplay}
        </span>
        {atCharLimit && (
          <span style={{ color: "#b91c1c" }}>
            Limit reached for your current plan. You can still delete sources, but cannot add more data.
          </span>
        )}
      </div>
      <div className="src-tabs" role="tablist" aria-label="Training sources">
        {["Links", "Files", "FAQs", "Spreadsheets"].map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            className={`src-tab ${tab === t ? "active" : ""}`}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {alert && (
        <div className={`src-alert ${alert.type}`} role="status">
          {alert.msg}
          <button className="src-alert-close" onClick={clearAlert} aria-label="Dismiss alert">×</button>
        </div>
      )}

      {/* LINKS */}
      {tab === "Links" && (
        <>
          <div className="src-section">
            <label className="src-label">Add URLs</label>
            <div className="src-row">
              <input
                value={linkInput}
                onChange={(e) => setLinkInput(e.target.value)}
                placeholder="https://example.com/page"
                className="src-input"
              />
              <button className="btn" onClick={addLink}>Add</button>
            </div>

            {links.length > 0 && (
              <>
                <div className="chip-wrap">
                  {links.map((u, i) => (
                    <span key={i} className="chip" title={u}>
                      <span className="chip-dot" />
                      <span className="chip-text">{u}</span>
                      <button className="chip-x" onClick={() => removeLink(i)} aria-label={`Remove ${u}`}>×</button>
                    </span>
                  ))}
                </div>

                <div className="src-actions">
                  <button className="btn primary" onClick={submitLinks} disabled={submitting || atCharLimit}>
                    {submitting ? <span className="spinner" /> : null}
                    Submit & Rebuild ({links.length})
                  </button>
                  <button className="btn ghost" onClick={() => setLinks([])} disabled={submitting}>
                    Clear
                  </button>
                </div>
              </>
            )}

            {links.length === 0 && (
              <div className="hint">
                Tip: Add your homepage and important subpages (Features, Approach, How it Works, FAQ, Contact).
              </div>
            )}
          </div>

          <LinkHistory />
        </>
      )}

      {/* FILES */}
      {tab === "Files" && (
        <>
          <div className="src-section">
            <p className="src-sub">Upload PDFs, DOCX, or TXT. We’ll extract text automatically.</p>

            <label className="dropzone"
              onDragOver={preventDefault}
              onDrop={(e) => onDrop(e, "file")}>
              <input
                type="file"
                onChange={(e) => upload("file", e.target.files?.[0])}
                disabled={uploadBusy}
                hidden
              />
              <div className="dz-inner">
                <div className="dz-icon">📄</div>
                <div>
                  <div className="dz-title">Drag & drop your file here</div>
                  <div className="dz-sub">or click to browse</div>
                </div>
              </div>
              {uploadBusy && <div className="dz-busy"><span className="spinner" /> Uploading…</div>}
            </label>
          </div>

          <FileHistory />
        </>
      )}

      {/* FAQS */}
      {tab === "FAQs" && (
        <>
          <form className="src-section" onSubmit={submitFaq}>
            <label className="src-label">Question</label>
            <input
              value={faqQ}
              onChange={(e) => setFaqQ(e.target.value)}
              className="src-input"
              placeholder='e.g., "What services does Eagle QMS provide?"'
            />

            <label className="src-label">Answer</label>
            <textarea
              rows={4}
              value={faqA}
              onChange={(e) => setFaqA(e.target.value)}
              className="src-textarea"
              placeholder="Write a clear, factual answer the bot can quote."
            />

            <div className="src-actions">
              <button className="btn primary" type="submit" disabled={submitting}>
                {submitting ? <span className="spinner" /> : null}
                Add FAQ & Rebuild
              </button>
              <button className="btn ghost" type="button" onClick={() => { setFaqQ(""); setFaqA(""); }} disabled={submitting}>
                Clear
              </button>
            </div>
          </form>

          <FaqHistory />
        </>
      )}

      {/* SPREADSHEETS */}
      {tab === "Spreadsheets" && (
        <>
          <div className="src-section">
            <p className="src-sub">Upload CSV/XLSX. We index the first rows for retrieval.</p>

            <label className="dropzone"
              onDragOver={preventDefault}
              onDrop={(e) => onDrop(e, "sheet")}>
              <input
                type="file"
                accept=".csv,.xlsx"
                onChange={(e) => upload("sheet", e.target.files?.[0])}
                disabled={uploadBusy}
                hidden
              />
              <div className="dz-inner">
                <div className="dz-icon">📊</div>
                <div>
                  <div className="dz-title">Drag & drop your sheet here</div>
                  <div className="dz-sub">CSV or XLSX • or click to browse</div>
                </div>
              </div>
              {uploadBusy && <div className="dz-busy"><span className="spinner" /> Uploading…</div>}
            </label>
          </div>

          <SheetHistory />
        </>
      )}
    </div>
  );
};

export default SourcesTrainer;
